import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import PasswordInput from '../components/PasswordInput';
import '../components/StytchLogin.css';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get token from URL parameters - Stytch might use different parameter names
    const tokenFromUrl = searchParams.get('token') || 
                         searchParams.get('password_reset_token') || 
                         searchParams.get('stytch_token_type');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      console.log('Password reset token found:', tokenFromUrl.substring(0, 20) + '...');
    } else {
      // Debug: log all available parameters
      console.log('Available URL parameters:', Object.fromEntries(searchParams.entries()));
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiClient.post('/auth/stytch/password/reset/complete', {
        token: token,
        password: password
      });
      
      if (response.data.success) {
        setSuccess('Password reset successful! You are now logged in.');
        
        // If we got an access token, store it and redirect to dashboard
        if (response.data.access_token) {
          localStorage.setItem('auth_token', response.data.access_token);
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          // Otherwise redirect to login
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="stytch-login-container">
      <div className="stytch-login-wrapper">
        <div className="stytch-login-header">
          <h1>Reset Password</h1>
          <p>Enter your new password below</p>
          
          {/* Debug info - only show in development */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '12px', borderRadius: '4px'}}>
              <strong>Debug Info:</strong><br/>
              Token found: {token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No'}<br/>
              URL: {window.location.href}<br/>
              All URL params: {JSON.stringify(Object.fromEntries(searchParams.entries()))}
            </div>
          )}
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="stytch-alert stytch-alert-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="stytch-alert stytch-alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {/* Password Reset Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="stytch-login-form">
            <div className="stytch-form-group">
              <label>
                <i className="fas fa-lock"></i>
                New Password
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your new password"
                disabled={isLoading}
                minLength="8"
                autoComplete="new-password"
                showStrengthIndicator={true}
                userInputs={[]} // Could add email if available
              />
            </div>
            
            <div className="stytch-form-group">
              <label>
                <i className="fas fa-lock"></i>
                Confirm Password
              </label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                disabled={isLoading}
                minLength="8"
                autoComplete="new-password"
                confirmValue={password}
                showConfirmationStatus={true}
              />
            </div>
            
            <button 
              type="submit"
              className="stytch-btn stytch-btn-primary stytch-btn-full"
              disabled={isLoading || !token}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Resetting Password...
                </>
              ) : (
                <>
                  <i className="fas fa-key"></i>
                  Reset Password
                </>
              )}
            </button>
          </form>
        )}

        {/* Back to Login Link */}
        <div className="stytch-password-reset">
          <button 
            type="button"
            className="stytch-reset-link"
            onClick={() => navigate('/login')}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 